import { listingAPI } from '../lib/api';
import type { Property } from '../types';

export type { Property };

export interface PropertyCategoryOption {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
}

export interface PropertyTypeOption {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  categoryId: string;
}

export interface AuditLogEntry {
  id: string;
  propertyId: string;
  action: 'CREATED' | 'UPDATED' | 'STATUS_CHANGED' | 'DELETED';
  actorId: string;
  actorName: string;
  actorEmail: string;
  actorRole: string;
  metadata?: {
    changedFields?: string[];
    fromStatus?: string;
    toStatus?: string;
    [key: string]: unknown;
  } | null;
  createdAt: string;
}

export interface PropertyFilter {
  location?: string;
  search?: string;
  checkIn?: string;
  checkOut?: string;
  guests?: number;
  /** Backend params: priceMin / priceMax (NOT priceRange array) */
  priceMin?: number;
  priceMax?: number;
  category?: string;
  type?: string;
  spaceType?: string;
  /** Comma-separated amenity slugs, e.g. "wifi,parking" */
  amenities?: string;
  instantBooking?: boolean;
  sort?: 'price_asc' | 'price_desc' | 'rating' | 'newest';
  status?: 'DRAFT' | 'PENDING_REVIEW' | 'PUBLISHED' | 'SUSPENDED' | 'ARCHIVED';
  page?: number;
  limit?: number;
}

/** Matches backend createPropertySchema exactly */
export interface CreatePropertyRequest {
  name: string;
  description?: string;
  categoryId: string;
  typeId: string;
  spaceTypeId?: string;
  contactPhone?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  country: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  maxGuests?: number;
  totalUnits?: number;
  instantBooking?: boolean;
  checkInTime?: string;
  checkOutTime?: string;
  minStay?: number;
  maxStay?: number;
  yearBuilt?: number;
  totalFloors?: number;
  smokingAllowed?: boolean;
  petsAllowed?: boolean;
  partiesAllowed?: boolean;
  experienceTagIds?: string[];
  /** Required when PLATFORM_STAFF/ADMIN creates a property on behalf of an org (property.validation.ts) */
  organizationId?: string;
}

/** Matches backend createPricingSchema body */
export interface CreatePricingRequest {
  priceType: 'NIGHTLY' | 'WEEKLY' | 'MONTHLY';
  basePrice: number;
  currency: string;
  cleaningFee?: number;
  serviceFeePercent?: number;
  securityDeposit?: number;
}

export interface PropertyResponse {
  data: Property;
}

export interface PropertiesResponse {
  data: Property[];
  total: number;
  page: number;
  pageSize: number;
}

export const propertyService = {
  /**
   * Get all active property categories (public)
   */
  getCategories: () =>
    listingAPI.get<{ data: PropertyCategoryOption[] }>('/v1/properties/categories').then((r) => r.data.data),

  /**
   * Get all active property types, optionally filtered by categoryId (public)
   */
  getTypes: (categoryId?: string) =>
    listingAPI.get<{ data: PropertyTypeOption[] }>('/v1/properties/types', { params: categoryId ? { categoryId } : undefined }).then((r) => r.data.data),

  /**
   * Get paginated list of properties with filters
   */
  list: (filters?: PropertyFilter) =>
    listingAPI.get<PropertiesResponse>('/v1/properties', { params: filters }).then((r) => r.data),

  /**
   * Get single property by ID
   */
  getById: (id: string) =>
    listingAPI.get<PropertyResponse>(`/v1/properties/${id}`).then((r) => r.data),

  /**
   * Create new property — returns the created property with its ID
   */
  create: (data: CreatePropertyRequest) =>
    listingAPI.post<PropertyResponse>('/v1/properties', data),

  /**
   * Create base pricing for a property
   */
  createPricing: (propertyId: string, data: CreatePricingRequest) =>
    listingAPI.post(`/v1/pricing/properties/${propertyId}/pricing`, data),

  /**
   * Update property
   */
  update: (id: string, data: Partial<CreatePropertyRequest>) =>
    listingAPI.patch<PropertyResponse>(`/v1/properties/${id}`, data),

  /**
   * Delete property
   */
  delete: (id: string) =>
    listingAPI.delete(`/v1/properties/${id}`).then((r) => r.data),

  /**
   * Change property status (active, inactive, archived)
   */
  changeStatus: (id: string, status: string) =>
    listingAPI.patch(`/v1/properties/${id}/status`, { status }).then((r) => r.data),

  /**
   * Step 1 of the media upload flow — get a presigned S3 PUT URL.
   * Backend: POST /v1/media/presign { folder, propertyId, originalName, contentType }
   * Returns: { uploadUrl, s3Key, publicUrl, expiresInSeconds }
   */
  presignUpload: (params: { propertyId: string; originalName: string; contentType: string }) =>
    listingAPI.post<{ data: { uploadUrl: string; s3Key: string; publicUrl: string | null; expiresInSeconds: number } }>(
      '/v1/media/presign',
      { folder: 'properties/images', ...params },
    ).then((r) => r.data.data),

  /**
   * Step 2 of the media upload flow — create the DB record after PUT to S3 succeeds.
   * Backend: POST /v1/media/confirm { s3Key, propertyId, contentType, fileSize }
   */
  confirmUpload: (params: { s3Key: string; propertyId: string; contentType: string; fileSize: number }) =>
    listingAPI.post('/v1/media/confirm', params).then((r) => r.data),

  /**
   * Delete a media record (also removes from S3).
   * Backend: DELETE /v1/media/:mediaId
   */
  deletePhoto: (mediaId: string) =>
    listingAPI.delete(`/v1/media/${mediaId}`).then((r) => r.data),

  /**
   * List all media for a property (public).
   * Backend: GET /v1/media/properties/:propertyId/media
   */
  listMedia: (propertyId: string) =>
    listingAPI.get(`/v1/media/properties/${propertyId}/media`).then((r) => r.data),

  /**
   * Get property availability calendar
   */
  getAvailability: (propertyId: string, startDate: string, endDate: string) =>
    listingAPI.get(`/v1/properties/${propertyId}/availability`, {
      params: { startDate, endDate },
    }).then((r) => r.data),

  /**
   * Get property reviews
   */
  getReviews: (propertyId: string, page = 1, limit = 10) =>
    listingAPI.get(`/v1/properties/${propertyId}/reviews`, { params: { page, limit } }).then((r) => r.data),

  /**
   * Get property statistics
   */
  getStats: (propertyId: string) =>
    listingAPI.get(`/v1/properties/${propertyId}/stats`).then((r) => r.data),

  getAuditLog: (propertyId: string) =>
    listingAPI.get<{ data: AuditLogEntry[] }>(`/v1/properties/${propertyId}/audit`).then((r) => r.data.data),

  /**
   * Search properties by filters
   */
  search: (filters: PropertyFilter) =>
    listingAPI.get<PropertiesResponse>('/v1/search', { params: filters }).then((r) => r.data),

  /**
   * Get featured/recommended properties
   */
  getFeatured: (limit = 12) =>
    listingAPI.get<PropertiesResponse>('/v1/properties/featured', { params: { limit } }).then((r) => r.data),

  /**
   * Get user's properties
   */
  getMyProperties: (page = 1, limit = 10) =>
    listingAPI.get<PropertiesResponse>('/v1/properties/mine', { params: { page, limit } }).then((r) => r.data),

  /**
   * Delegated property management -- ORG_ADMIN grants an ORG_MANAGER/ORG_STAFF real access to a
   * property they didn't personally create.
   */
  listAssignments: (propertyId: string) =>
    listingAPI.get<{ data: Array<{ id: string; propertyId: string; userId: string; tenantId: string; createdAt: string }> }>(`/v1/properties/${propertyId}/assignments`).then((r) => r.data.data),

  assignUser: (propertyId: string, userId: string) =>
    listingAPI.post<{ data: unknown }>(`/v1/properties/${propertyId}/assignments`, { userId }).then((r) => r.data.data),

  unassignUser: (propertyId: string, userId: string) =>
    listingAPI.delete<{ data: unknown }>(`/v1/properties/${propertyId}/assignments/${userId}`).then((r) => r.data.data),

  /**
   * Get all space types (public)
   */
  getSpaceTypes: () =>
    listingAPI.get<{ data: { id: string; name: string; slug: string; description?: string }[] }>('/v1/properties/space-types').then((r) => r.data.data),

  /**
   * Get experience tags grouped by category (public)
   */
  getExperienceTags: () =>
    listingAPI.get<{ data: Record<string, { id: string; name: string; slug: string; group: string }[]> }>('/v1/properties/experience-tags').then((r) => r.data.data),

  /**
   * Replace experience tags for a property
   */
  setExperienceTags: (propertyId: string, tagIds: string[]) =>
    listingAPI.put(`/v1/properties/${propertyId}/experience-tags`, { tagIds }).then((r) => r.data),

  /**
   * Upload a photo: presign → PUT to S3 → confirm
   */
  uploadPhoto: async (propertyId: string, file: File): Promise<void> => {
    // Some browsers/OS leave file.type empty — derive from extension as fallback
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const MIME_MAP: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg',
      png: 'image/png', webp: 'image/webp', gif: 'image/gif',
    };
    const contentType = file.type || MIME_MAP[ext] || 'image/jpeg';

    const { uploadUrl, s3Key } = await propertyService.presignUpload({
      propertyId,
      originalName: file.name,
      contentType,
    });
    const putResp = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!putResp.ok) throw new Error(`S3 upload failed: ${putResp.status} ${putResp.statusText}`);
    await propertyService.confirmUpload({
      s3Key,
      propertyId,
      contentType,
      fileSize: file.size,
    });
  },
};
